import {
  PAGE_API_QUERY_KEY_HEAD,
  RECORD_DEPENDENT_API_QUERY_KEY_HEAD
} from '@Core/clients/pageApiQueryKeys';
import { type QueryClient } from '@tanstack/react-query';

/**
 * Query-key heads whose cached entries are scoped to a single record's
 * soft-delete state. After a delete or restore flips that state, force-
 * refetching these for the same record resolves it under the wrong subset:
 * `ShowRecord` defaults to the active scope, `delete-preview` excludes
 * soft-deleted records, and `restore-preview` requires an archived record.
 * Any of those refetches 404s the instant the mutation succeeds, which is
 * the noise users see in the network tab right before navigation unmounts
 * the page.
 */
const PER_RECORD_QUERY_KEY_HEADS = new Set([
  'record',
  'record-delete-preview',
  'record-restore-preview'
]);

/**
 * Every query-key head under which cached **record data** lives. Mutating a
 * record can change any of them (a list gains a row, a relation picker's
 * options shift, a calendar event moves), so they are all refreshed together.
 * Schema, policy, and label caches are deliberately absent — record writes
 * never change them, and invalidating them turns one write into a burst of
 * unrelated refetches.
 */
const RECORD_DATA_QUERY_KEY_HEADS = new Set([
  'calendar',
  'record',
  'record-delete-preview',
  'record-dynamic-field-selected-options',
  'record-options',
  'record-restore-preview',
  'records'
]);

function isRecordDataQuery(queryKey: readonly unknown[]): boolean {
  const [head, apiKeyHead] = queryKey;

  return (
    (typeof head === 'string' && RECORD_DATA_QUERY_KEY_HEADS.has(head)) ||
    (head === PAGE_API_QUERY_KEY_HEAD && apiKeyHead === RECORD_DEPENDENT_API_QUERY_KEY_HEAD)
  );
}

function isAnalyticsQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'analytics';
}

function isAffectedByRecordMutation(queryKey: readonly unknown[]): boolean {
  return isRecordDataQuery(queryKey) || isAnalyticsQuery(queryKey);
}

/**
 * Invalidate every cached query holding record data or analytics, leaving
 * schema/policy/label caches alone. Both are cross-resource because relation-backed
 * displays, filters, and aggregations can depend on records from another resource.
 * Used after writes with no single subject record to protect — create, update, and
 * author-triggered refreshes. Delete and restore use
 * {@link invalidateRecordCachesExceptSubject}, which applies this same scope and
 * additionally skips the mutated record.
 */
export function invalidateRecordCaches(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => isAffectedByRecordMutation(query.queryKey)
  });
}

interface InvalidateRecordCachesOptions {
  /**
   * The record returned by the mutation (e.g. the restored record). When
   * provided it is merged into the subject's cached `record` entries so any
   * still-mounted consumer reflects the new state immediately — without a
   * network refetch that would 404 under the now-flipped soft-delete scope.
   * Merging (rather than replacing) preserves relation data the mutation
   * response does not include.
   */
  subjectRecord?: Record<string, unknown> | null;
}

/**
 * Invalidate cached record queries after a delete/restore so lists, counts,
 * and other records refresh — while skipping the just-mutated record's own
 * per-record queries to avoid the scope-mismatched 404 refetch described
 * above. The dialog closes and the page navigates immediately after, so the
 * subject's stale per-record entries are dropped naturally.
 *
 * Uses the same affected-query predicate as {@link invalidateRecordCaches}, so all
 * four record mutations refresh record data and analytics across resources; the
 * subject skip is the only thing separating them.
 *
 * When `subjectRecord` is supplied the subject's `record` cache is updated in
 * place first, so a non-navigating consumer still shows fresh data.
 */
export function invalidateRecordCachesExceptSubject(
  queryClient: QueryClient,
  resourceId: string | undefined,
  recordId: string | undefined,
  options: InvalidateRecordCachesOptions = {}
): Promise<void> {
  const { subjectRecord } = options;

  if (subjectRecord) {
    queryClient.setQueriesData<Record<string, unknown>>(
      { queryKey: ['record', resourceId, recordId] },
      (existing) =>
        existing && typeof existing === 'object' && !Array.isArray(existing)
          ? { ...existing, ...subjectRecord }
          : subjectRecord
    );
  }

  return queryClient.invalidateQueries({
    predicate: (query) => {
      const [head, keyResourceId, keyRecordId] = query.queryKey as unknown[];

      const isSubjectPerRecordQuery =
        typeof head === 'string' &&
        PER_RECORD_QUERY_KEY_HEADS.has(head) &&
        keyResourceId === resourceId &&
        keyRecordId === recordId;

      return isAffectedByRecordMutation(query.queryKey) && !isSubjectPerRecordQuery;
    }
  });
}
